import * as React from "react";
import { useFloating, autoUpdate, offset, flip, size, useRole, useClick, useDismiss, useInteractions, useTransitionStatus, FloatingPortal, FloatingFocusManager } from "@floating-ui/react";
import clsx from "clsx";
import { Button } from "../button/button.mjs";
import { CollapseToggle } from "../collapse-toggle/collapse-toggle.mjs";
import { HeaderContext } from "../helpers/header-provider/header-provider.mjs";
import "debounce";
import "tabbable";
import { useResponsiveProps } from "../helpers/helper/hooks/use-responsive-props.mjs";
import { PropsChecker } from "../helpers/props-checker/props-checker.mjs";
import { Suggestion } from "../suggestion/suggestion.mjs";
import { useTheme } from "../helpers/theme-provider/use-theme.mjs";
const Dropdown = (props) => {
  const innerComponent = props.button.responsive ? /* @__PURE__ */ React.createElement(DropdownResponsive, { ...props, buttonResponsive: props.button.responsive }) : /* @__PURE__ */ React.createElement(DropdownInner, { ...props });
  return /* @__PURE__ */ React.createElement(PropsChecker, { componentName: "Dropdown", componentProps: props, defaultValueName: "defaultIsOpen", valueName: "isOpen", onChangeNames: "onToggle" }, innerComponent);
};
const DropdownResponsive = (props) => {
  const { buttonResponsive: responsive, button, ...rest } = props;
  const responsiveProps = useResponsiveProps({
    responsive
  });
  return /* @__PURE__ */ React.createElement(DropdownInner, { ...rest, button: { ...button, ...responsiveProps } });
};
const DropdownInner = (props) => {
  const { align, button, className, defaultIsOpen, id, isOpen: isOpenProp, onClose, onToggle, width = "default", outsideClickCloseFilter } = props;
  const [isOpenState, setIsOpenState] = React.useState(defaultIsOpen || false);
  const { portalRoot } = useTheme();
  const buttonRef = React.useRef(null);
  const isOpenControlled = (isOpen2 = props.isOpen) => {
    return !!onToggle && typeof isOpen2 !== "undefined";
  };
  const { headerHeight, isHeaderHidden, headerBottomHeight } = React.useContext(HeaderContext);
  const isOpen = isOpenControlled(isOpenProp) ? isOpenProp : isOpenState;
  const padding = {
    top: isHeaderHidden ? (
      // employee header is always visible
      headerHeight - headerBottomHeight + 8
    ) : headerHeight + 8,
    right: 8,
    bottom: 8,
    left: 8
  };
  const { refs, context, floatingStyles } = useFloating({
    placement: align === "end" ? "bottom-end" : "bottom-start",
    middleware: [
      offset(0),
      flip({
        padding
      }),
      size({
        padding,
        apply({ availableWidth, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: "".concat(availableWidth, "px"),
            maxHeight: "".concat(Math.max(100, availableHeight), "px")
          });
        }
      })
    ],
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: (nextOpen) => {
      if (!isOpenControlled()) {
        setIsOpenState(nextOpen);
      }
      if (onToggle) {
        onToggle(nextOpen);
      }
      if (!nextOpen && isOpen && onClose) {
        onClose();
      }
    }
  });
  const role = useRole(context, { role: "dialog" });
  const click = useClick(context, { toggle: true });
  const dismiss = useDismiss(context, {
    outsidePressEvent: "mousedown",
    enabled: isOpen,
    outsidePress: (event) => {
      const clickedOnOutsideTrigger = event.target instanceof Element && (outsideClickCloseFilter == null ? void 0 : outsideClickCloseFilter(event.target));
      if (clickedOnOutsideTrigger) {
        return false;
      }
      return true;
    }
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
  const getZIndex = () => {
    if (buttonRef.current) {
      const inHeader = buttonRef.current.closest(".header") !== null;
      const inModal = buttonRef.current.closest(".modal") !== null;
      if (inHeader || inModal) {
        return 30;
      }
    }
    return 19;
  };
  const { isMounted, status } = useTransitionStatus(context, {
    duration: 120
  });
  const renderDropdown = () => {
    if (!isMounted) {
      return null;
    }
    return /* @__PURE__ */ React.createElement(FloatingPortal, { root: props.portalContainer || portalRoot }, /* @__PURE__ */ React.createElement(FloatingFocusManager, { context, initialFocus: -1, modal: false, returnFocus: true }, /* @__PURE__ */ React.createElement(
      "div",
      {
        ref: refs.setFloating,
        className: clsx("dropdown", "is-".concat(status), "dropdown--width-".concat(width), className),
        style: {
          ...floatingStyles,
          zIndex: getZIndex()
        },
        ...getFloatingProps({
          onClick: (e) => {
            e.stopPropagation();
          }
        })
      },
      /* @__PURE__ */ React.createElement(Suggestion, { id, items: props.items, className: "dropdown__suggestion" })
    )));
  };
  const renderButtonOrCollapseToggle = () => {
    const buttonProps = {
      ...button,
      ref: (el) => {
        buttonRef.current = el;
        refs.setReference(el);
      },
      isActive: isOpen,
      ...getReferenceProps({
        "aria-controls": isOpen ? id : void 0
      })
    };
    if (button.iconRight && button.iconRight === "#arrow-down") {
      return /* @__PURE__ */ React.createElement(CollapseToggle, { ...buttonProps });
    }
    return /* @__PURE__ */ React.createElement(Button, { ...buttonProps });
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, renderButtonOrCollapseToggle(), renderDropdown());
};
export {
  Dropdown
};
